import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BoxHolder from '../../components/shared/BoxHolder/BoxHolder';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import Loading from '../../components/shared/Loading';
import BtButton from '../bt-button/bt-button';
import { serviceEvaluations, SupplierSummary } from '../../services/service-evaluations';
import { TAG_ARCHIVED_LABEL } from '../../util/evaluationStatusMeta';
import EvaluationsLayout from './EvaluationsLayout';
import ScoreOverview from './ScoreOverview';
import ScoreBarList, { ScoreBarRow } from './ScoreBarList';
import CommentList from './CommentList';
import EvaluationTable from './EvaluationTable';
import ResponseModal from './ResponseModal';
import { reportUnexpected } from './reportUnexpected';
import styles from './page-supplier-evaluations.module.scss';

type Props = {
  supplierId: string;
  registerUrl: string;
  statusUrlOf: (requestId: string) => string;
};

type LoadFailure = 'notFound' | 'failed';

const isEmpty = (summary: SupplierSummary) => !summary.evaluationCount && !summary.comments.length;

const SupplierEvaluationReview: FC<Props> = ({ supplierId, registerUrl, statusUrlOf }) => {
  const [t] = useTranslation('evaluations');
  const [summary, setSummary] = useState<SupplierSummary | null>(null);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [openResponseId, setOpenResponseId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSummary(await serviceEvaluations.getSupplierSummary(supplierId));
      setFailure(null);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setFailure('notFound');
        return;
      }
      reportUnexpected(error);
      setFailure('failed');
    }
  }, [supplierId]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = () => {
    setFailure(null);
    setSummary(null);
    load();
  };

  const supplierName = summary?.supplier.name;
  const path = [t('review.breadcrumb.suppliers', 'Leverantörer'), supplierName].filter(
    (segment): segment is string => Boolean(segment),
  );

  const renderContent = () => {
    if (failure === 'notFound') {
      return (
        <p className={styles.loadError} role="alert">
          {t('review.notFound', 'Leverantören hittades inte.')}
        </p>
      );
    }
    if (failure) {
      return (
        <div className={styles.loadError} role="alert">
          <span>{t('review.loadFailed', 'Utvärderingarna kunde inte hämtas.')}</span>
          <BtButton size="xxs" onClick={retry}>
            {t('review.retry', 'Försök igen')}
          </BtButton>
        </div>
      );
    }
    if (!summary) return <Loading type="inline" />;

    const tagRows: ScoreBarRow[] = [
      ...summary.byTag.map(tag => ({
        key: tag.tagId,
        label: tag.archived
          ? `${tag.name} · ${t(TAG_ARCHIVED_LABEL.labelKey, TAG_ARCHIVED_LABEL.fallback)}`
          : tag.name,
        score: tag.score,
        count: tag.count,
      })),
      ...(summary.untagged.count
        ? [
            {
              key: 'untagged',
              label: t('review.tags.untagged', 'Utan tagg'),
              score: summary.untagged.score,
              count: summary.untagged.count,
            },
          ]
        : []),
    ];

    return (
      <>
        <BoxHolder className={styles.card}>
          <ScoreOverview summary={summary} />
        </BoxHolder>
        {summary.requests.length > 0 && (
          <BoxHolder className={styles.card}>
            <h3 className={styles.cardTitle}>{t('review.evaluations.title', 'Utvärderingar')}</h3>
            <div className={styles.table}>
              <EvaluationTable requests={summary.requests} statusUrlOf={statusUrlOf} />
            </div>
          </BoxHolder>
        )}
        {isEmpty(summary) ? (
          <InfoBox
            title={t('review.empty.title', 'Inga utvärderingar än')}
            description={t(
              'review.empty.description',
              'När någon har svarat visas betyg per tagg och kommentarer här.',
            )}
          />
        ) : (
          <>
            {summary.byTag.length > 0 && (
              <BoxHolder className={styles.card}>
                <h3 className={styles.cardTitle}>{t('review.tags.title', 'Per tagg')}</h3>
                <ScoreBarList rows={tagRows} />
                <span className={`${styles.muted} ${styles.small}`}>
                  {t(
                    'review.tags.footnote',
                    'Ett svar räknas i varje tagg som utvärderingen har, så taggarna summerar inte till totalbetyget.',
                  )}
                </span>
              </BoxHolder>
            )}
            <BoxHolder className={styles.card}>
              <h3 className={styles.cardTitle}>{t('review.comments.title', 'Kommentarer')}</h3>
              <CommentList comments={summary.comments} onShowAnswer={setOpenResponseId} />
            </BoxHolder>
          </>
        )}
        {openResponseId && (
          <ResponseModal
            responseId={openResponseId}
            onClose={() => setOpenResponseId(null)}
            onHidden={load}
          />
        )}
      </>
    );
  };

  return (
    <EvaluationsLayout path={path} backTo={registerUrl}>
      {renderContent()}
    </EvaluationsLayout>
  );
};

export default SupplierEvaluationReview;
